import { ChatClient, ChatMessage, ChatMessageDeletedEvent, ChatMessageType, ChatThreadClient } from "@azure/communication-chat";
import { ChatThreadClientState, useChatClient } from "@azure/communication-react";
import { useMemo } from "react";
import { ChatMessageEditedEvent, ChatMessageReceivedEvent } from "@azure/communication-signaling";
import { usePcpChatThreadState } from "../store/slices/chatThreadStateSlice";

export async function initializeThreadState(chatThreadClient: ChatThreadClient): Promise<void> {
    const props = await chatThreadClient.getProperties();

    if (props.deletedOn) {
        // thread is deleted
        return;
    }

    for await (const _page of chatThreadClient.listParticipants().byPage()) {
        // Simply fetching participants updates the cached state in client.
    }
}

export  async function fetchThreads(chatClient: ChatClient) {
    // NOTE: this overlaps with `useRefreshThreadStates`, which also calls
    // `initializeThreadState` per thread around startup. The redundant double-init
    // is intentionally left as a follow-up (deduping requires runtime mount-order
    // tracing — see the PR #3 plan). Here we only parallelize the per-thread init.
    const threadIds: string[] = [];
    for await (const page of chatClient.listChatThreads().byPage()) {
        // fetching threads is sufficient. State will be saved in the chatClient internally.
        for await (const thread of page) {
            // skip deleted threads
            if (thread.deletedOn) {
                continue;
            }
            threadIds.push(thread.id);
        }
    }

    // Initialize all thread states in parallel; isolate per-thread failures so one
    // rejection doesn't abort the rest of the batch.
    await Promise.all(
        threadIds.map(async (threadId) => {
            try {
                const threadClient = chatClient.getChatThreadClient(threadId);
                await initializeThreadState(threadClient);
            } catch (error) {
                console.error(error);
            }
        })
    );
}

export const useActivatedChatThreadTopicName = (transformTopicFn: (topic?: string) => string) => {
    const chatClient = useChatClient();
    const threadState = usePcpChatThreadState();
    const activeThreadId = threadState.activeThread;
    const chatState = chatClient.getState();
    const threads = chatState.threads;

    return useMemo(() => {
        if (!activeThreadId) {
            return "";
        }

        const activeThread: undefined | ChatThreadClientState = threads[activeThreadId];
        const topic = activeThread?.properties?.topic;

        return transformTopicFn ? transformTopicFn(topic) : topic;
    }, [threads, activeThreadId, transformTopicFn]);
}



export const convertEventToChatMessage = (
    event: ChatMessageReceivedEvent | ChatMessageEditedEvent | ChatMessageDeletedEvent
): ChatMessage => ({
        id: event.id,
        version: event.version,
        content: isChatMessageDeletedEvent(event) ? undefined : { message: event.message },
        type: convertEventType(event.type),
        sender: event.sender,
        senderDisplayName: event.senderDisplayName,
        sequenceId: '',
        createdOn: new Date(event.createdOn),
        editedOn: isChatMessageEditedEvent(event) ? event.editedOn : undefined,
        deletedOn: isChatMessageDeletedEvent(event) ? event.deletedOn : undefined
    });

const isChatMessageEditedEvent = (
    event: ChatMessageReceivedEvent | ChatMessageEditedEvent | ChatMessageDeletedEvent
): event is ChatMessageEditedEvent => 'editedOn' in event;

const isChatMessageDeletedEvent = (
    event: ChatMessageReceivedEvent | ChatMessageEditedEvent | ChatMessageDeletedEvent
): event is ChatMessageDeletedEvent => 'deletedOn' in event;

// only text/html message type will be received from event
const convertEventType = (type: string): ChatMessageType => {
    const lowerCaseType = type.toLowerCase();
    if (lowerCaseType === 'richtext/html' || lowerCaseType === 'html') {
        return 'html';
    } else {
        return 'text';
    }
};
